import { useNode, useEditor } from "@craftjs/core";
import React from "react";
import { Box } from "@mui/material";
import { useInlineEdit } from "../../hooks/useInlineEdit";
import { selectedOutlineSx } from "../../config/settingsStyles";
import { TextField, SelectField, SliderField, ColorField, SettingsSection, SettingsRow } from "../ui/form/SettingsField";

export interface NavCTAProps {
  text?: string;
  href?: string;
  backgroundColor?: string;
  textColor?: string;
  borderRadius?: number;
  paddingX?: number;
  paddingY?: number;
  fontSize?: number;
  fontWeight?: number;
}

export const NavCTA = ({
  text = "Book Now",
  href = "/contact",
  backgroundColor = "var(--color-primary, #3b82f6)",
  textColor = "#ffffff",
  borderRadius = 8,
  paddingX = 20,
  paddingY = 8,
  fontSize = 14,
  fontWeight = 500,
}: NavCTAProps) => {
  const {
    connectors: { connect, drag },
    selected,
    actions: { setProp },
  } = useNode((state) => ({
    selected: state.events.selected,
  }));

  const { getEditableProps } = useInlineEdit(setProp);

  const { enabled: editorEnabled } = useEditor((state) => ({
    enabled: state.options.enabled,
  }));

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (editorEnabled) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  return (
    <Box
      ref={(ref: HTMLDivElement | null) => {
        if (ref) connect(drag(ref));
      }}
      sx={{
        flexShrink: 0,
        ...(selected && { ...selectedOutlineSx, outlineOffset: "2px" }),
      }}
    >
      <Box
        component="a"
        href={href}
        onClick={handleClick}
        {...getEditableProps("text", text || "", (v) => { setProp((p: NavCTAProps) => { p.text = v; }); })}
        sx={{
          display: "inline-block",
          color: textColor,
          backgroundColor: backgroundColor,
          px: `${paddingX}px`,
          py: `${paddingY}px`,
          borderRadius: `${borderRadius}px`,
          fontWeight: fontWeight,
          fontSize: `${fontSize}px`,
          textDecoration: "none",
          whiteSpace: "nowrap",
          transition: "opacity 0.2s",
          "&:hover": { opacity: 0.9 },
        }}
      >
        {text}
      </Box>
    </Box>
  );
};

export const NavCTASettings = () => {
  const {
    actions: { setProp },
    text,
    href,
    backgroundColor,
    textColor,
    borderRadius,
    paddingX,
    paddingY,
    fontSize,
    fontWeight,
  } = useNode((node) => ({
    text: node.data.props.text,
    href: node.data.props.href,
    backgroundColor: node.data.props.backgroundColor,
    textColor: node.data.props.textColor,
    borderRadius: node.data.props.borderRadius,
    paddingX: node.data.props.paddingX,
    paddingY: node.data.props.paddingY,
    fontSize: node.data.props.fontSize,
    fontWeight: node.data.props.fontWeight,
  }));

  return (
    <SettingsSection>
      <TextField label="Button Text" value={text} onChange={(v) => setProp((props: NavCTAProps) => (props.text = v))} />

      <TextField label="Link URL" value={href} onChange={(v) => setProp((props: NavCTAProps) => (props.href = v))} />

      <div>
        <ColorField
          label="Background Color"
          value={backgroundColor.startsWith("var") ? "#3b82f6" : backgroundColor}
          onChange={(v) => setProp((props: NavCTAProps) => (props.backgroundColor = v))}
        />
        <button
          onClick={() => setProp((props: NavCTAProps) => (props.backgroundColor = "var(--color-primary, #3b82f6)"))}
          style={{
            marginTop: "4px",
            width: "100%",
            padding: "8px",
            backgroundColor: "#f0fdf4",
            color: "#16a34a",
            border: "1px solid #bbf7d0",
            borderRadius: "4px",
            cursor: "pointer",
            fontSize: "0.75rem",
          }}
        >
          Use Theme Primary
        </button>
      </div>

      <ColorField
        label="Text Color"
        value={textColor}
        onChange={(v) => setProp((props: NavCTAProps) => (props.textColor = v))}
      />

      <SliderField
        label="Border Radius"
        value={borderRadius}
        onChange={(v) => setProp((props: NavCTAProps) => (props.borderRadius = v))}
        min={0}
        max={24}
        unit="px"
      />

      <SettingsRow>
        <SliderField
          label="Padding X"
          value={paddingX}
          onChange={(v) => setProp((props: NavCTAProps) => (props.paddingX = v))}
          min={8}
          max={32}
          unit="px"
        />
        <SliderField
          label="Padding Y"
          value={paddingY}
          onChange={(v) => setProp((props: NavCTAProps) => (props.paddingY = v))}
          min={4}
          max={16}
          unit="px"
        />
      </SettingsRow>

      <SliderField
        label="Font Size"
        value={fontSize}
        onChange={(v) => setProp((props: NavCTAProps) => (props.fontSize = v))}
        min={12}
        max={18}
        unit="px"
      />

      <SelectField
        label="Font Weight"
        value={String(fontWeight)}
        onChange={(v) => setProp((props: NavCTAProps) => (props.fontWeight = parseInt(v)))}
        options={[
          { value: "400", label: "Normal (400)" },
          { value: "500", label: "Medium (500)" },
          { value: "600", label: "Semibold (600)" },
          { value: "700", label: "Bold (700)" },
        ]}
      />
    </SettingsSection>
  );
};

NavCTA.craft = {
  displayName: "Nav CTA",
  props: {
    text: "Book Now",
    href: "/contact",
    backgroundColor: "var(--color-primary, #3b82f6)",
    textColor: "#ffffff",
    borderRadius: 8,
    paddingX: 20,
    paddingY: 8,
    fontSize: 14,
    fontWeight: 500,
  },
  rules: {
    canDrag: () => true,
  },
  related: {
    settings: NavCTASettings,
  },
};
